export const MCP_HOST_URL = "https://mcp.botspot.trade";
export const MCP_SERVER_URL = "https://mcp.botspot.trade/mcp";
export const MCP_PROTOCOL_VERSION = "2026-07-28";

export type AgentStepPart =
  | string
  | Readonly<{ kind: "strong" | "code"; text: string }>;

export type AgentSetupClient = Readonly<{
  id: "chatgpt" | "claude" | "claude-code" | "codex" | "gemini" | "cursor";
  name: string;
  context: string;
  auth: string;
  bestFor: string;
  copyValue?: string;
  copyLabel?: string;
  code?: string;
  steps: readonly Readonly<{ label: string; body: readonly AgentStepPart[] }>[];
}>;

const codeBlocks = {
  claudeCode: `claude mcp add --transport http botspot https://mcp.botspot.trade/mcp --header "Authorization: Bearer $BOTSPOT_API_KEY"`,
  codex: `[mcp_servers.botspot]
url = "https://mcp.botspot.trade/mcp"
bearer_token_env_var = "BOTSPOT_API_KEY"`,
  gemini: `gemini mcp add --transport http botspot https://mcp.botspot.trade/mcp`,
  cursor: `{
  "mcpServers": {
    "botspot": {
      "url": "https://mcp.botspot.trade/mcp",
      "headers": {
        "Authorization": "Bearer botspot_YOUR_API_KEY"
      }
    }
  }
}`,
} as const;

const strong = (text: string): AgentStepPart => ({ kind: "strong", text });
const code = (text: string): AgentStepPart => ({ kind: "code", text });

export const agentSetupClients: readonly AgentSetupClient[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    context: "OAuth connector",
    auth: "OAuth",
    bestFor: "Asking ChatGPT to inspect BotSpot strategies, broker context, backtests, and deployment state.",
    copyValue: MCP_SERVER_URL,
    copyLabel: "Copy ChatGPT Server URL",
    steps: [
      { label: "Enable developer mode", body: ["Use ChatGPT on the web. Open ", strong("Settings -> Security and login"), ", then enable ", strong("Developer mode"), ". Availability can depend on account and workspace policy."] },
      { label: "Add BotSpot", body: ["Open ", strong("ChatGPT Plugins"), ", select the plus button, and create a connection named ", strong("BotSpot"), "."] },
      { label: "Enter server URL", body: ["Choose the public server URL connection method and paste ", code(MCP_SERVER_URL), ". Keep the complete ", code("/mcp"), " path."] },
      { label: "Authorize", body: ["Create the connection, complete BotSpot OAuth when prompted, then review the discovered tools and confirmation behavior."] },
      { label: "Use it in chat", body: ["Start a new chat and select BotSpot from the tools menu, or refer to BotSpot in your prompt."] },
    ],
  },
  {
    id: "claude",
    name: "Claude",
    context: "Remote MCP",
    auth: "OAuth",
    bestFor: "Keeping Claude connected to the same BotSpot strategy and execution context you use in the app.",
    copyValue: MCP_HOST_URL,
    copyLabel: "Copy Claude MCP URL",
    steps: [
      { label: "Add BotSpot", body: ["Individual users open ", strong("Customize -> Connectors"), ", click ", strong("+ -> Add custom connector"), " and paste the MCP host URL. Team and Enterprise owners add it first under ", strong("Organization settings -> Connectors"), "."] },
      { label: "Authorize", body: ["Complete OAuth, then enable BotSpot for the conversation from ", strong("+ -> Connectors"), "."] },
    ],
  },
  {
    id: "claude-code",
    name: "Claude Code",
    context: "CLI setup",
    auth: "BotSpot API key",
    bestFor: "Working from a terminal while still referencing your BotSpot library, revisions, and backtest evidence.",
    code: codeBlocks.claudeCode,
    copyLabel: "Copy Claude Code command",
    steps: [
      { label: "Create a key", body: ["Create a BotSpot API key and export it as ", code("BOTSPOT_API_KEY"), "."] },
      { label: "Install MCP", body: ["Run the command below from your shell."] },
    ],
  },
  {
    id: "codex",
    name: "Codex",
    context: "CLI, desktop, and IDE setup",
    auth: "BotSpot API key",
    bestFor: "Researching markets and working with your BotSpot strategies, revisions, and backtest evidence from Codex.",
    code: codeBlocks.codex,
    copyLabel: "Copy Codex config",
    steps: [
      { label: "Create a key", body: ["Create a BotSpot API key and export it as ", code("BOTSPOT_API_KEY"), "."] },
      { label: "Configure BotSpot", body: ["Add the configuration below to ", code("~/.codex/config.toml"), " or a trusted project's ", code(".codex/config.toml"), "."] },
      { label: "Confirm the connection", body: ["Open Codex and use ", code("/mcp"), ", or run ", code("codex mcp list"), "."] },
    ],
  },
  {
    id: "gemini",
    name: "Gemini CLI",
    context: "CLI setup",
    auth: "OAuth",
    bestFor: "Using Gemini CLI with the same BotSpot strategies, broker context, backtests, and trading workflows.",
    code: codeBlocks.gemini,
    copyLabel: "Copy Gemini CLI command",
    steps: [
      { label: "Add BotSpot", body: ["Run the command below to add the hosted BotSpot MCP server."] },
      { label: "Authorize", body: ["Let Gemini CLI discover BotSpot OAuth, then complete the browser login when prompted. Use ", code("/mcp auth botspot"), " if authentication is still required."] },
      { label: "Confirm the connection", body: ["Run ", code("gemini mcp list"), " and check that BotSpot is connected."] },
    ],
  },
  {
    id: "cursor",
    name: "Cursor",
    context: "IDE setup",
    auth: "BotSpot API key",
    bestFor: "Bringing BotSpot strategy context into a code editor without copying credentials into prompts.",
    code: codeBlocks.cursor,
    copyLabel: "Copy Cursor MCP config",
    steps: [
      { label: "Choose scope", body: ["Use ", code(".cursor/mcp.json"), " for one project or ", code("~/.cursor/mcp.json"), " globally."] },
      { label: "Add BotSpot", body: ["Paste the config below and replace the placeholder with a BotSpot API key."] },
    ],
  },
] as const;
